/**
 * AB#1904 (W-1) — Sibling of `BranchesSection.upgradeCard` for the
 * Manage Staff section. Shown when the owner has reached their plan's
 * staff cap. Matches the visual treatment of the branches cap card.
 *
 * Props lifted so the section file (already 1240 LOC) doesn't have to
 * know the i18n keys or the attribution URL shape.
 *
 * PR #1312 iteration — Omar Finding #2: all user-facing strings tokenized
 * through `t()` using the existing `subscription.cap.*` key family.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackEvent } from '../../lib/analytics';
import s from './StaffCapUpgradeCard.module.css';

/** Plan-tier ladder, used to resolve `nextPlan` for the upgrade-card copy.
 *  Must stay in lock-step with `ManageStaffSection.PLAN_STAFF_CAPS`. The
 *  BE is the source of truth on the cap values; this map only drives the
 *  "what plan comes next" labelling. */
const STAFF_TIER_LADDER: Array<{ plan: string; cap: number }> = [
    { plan: 'Starter', cap: 2 },
    { plan: 'Growth', cap: 7 },
    { plan: 'Pro', cap: 20 },
    { plan: 'Enterprise', cap: Infinity },
];

function resolveNextTier(currentPlan: string): { plan: string; cap: number } {
    const i = STAFF_TIER_LADDER.findIndex(t => t.plan === currentPlan);
    if (i < 0 || i >= STAFF_TIER_LADDER.length - 1) {
        // Owner is on Enterprise (or unknown plan) — no "next" tier.
        // Fall back to Enterprise label; copy still reads sensibly.
        return STAFF_TIER_LADDER[STAFF_TIER_LADDER.length - 1];
    }
    return STAFF_TIER_LADDER[i + 1];
}

export interface StaffCapUpgradeCardProps {
    planName: string;
    currentCap: number;
}

const StaffCapUpgradeCard: React.FC<StaffCapUpgradeCardProps> = ({
    planName,
    currentCap,
}) => {
    const { t } = useLanguage();

    React.useEffect(() => {
        trackEvent('subscription.tierCapHit', {
            capType: 'staff',
            currentPlan: planName,
        });
    }, [planName]);

    const nextTier = resolveNextTier(planName);
    const nextCapUnlimited = !Number.isFinite(nextTier.cap);
    const bodyKey = nextCapUnlimited
        ? 'subscription.cap.staffBodyUnlimited'
        : 'subscription.cap.staffBody';

    const title = t('subscription.cap.staffTitle')
        .replace('{n}', String(currentCap + 1));
    const body = t(bodyKey)
        .replace('{plan}', planName)
        .replace('{cap}', String(currentCap))
        .replace('{nextPlan}', nextTier.plan)
        .replace('{nextCap}', nextCapUnlimited ? '' : String(nextTier.cap));

    return (
        <div className={s.upgradeCard}>
            <div className={s.upgradeCardIcon} aria-hidden="true">✨</div>
            <div className={s.upgradeCardBody}>
                <div className={s.upgradeCardTitle}>{title}</div>
                <div className={s.upgradeCardDesc}>{body}</div>
            </div>
            <Link
                to="/subscription?from=staff&feature=larger-team"
                className={s.upgradeCardCta}
            >
                {t('subscription.cap.cta')}
            </Link>
        </div>
    );
};

export default StaffCapUpgradeCard;
